import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { CloseIcon } from "@chakra-ui/icons";
import { Box, HStack, IconButton, Tooltip, useDisclosure, useToast } from "@chakra-ui/react";
import { CreatableSelect, chakraComponents } from "chakra-react-select";

import { PropertyConfirmModal } from "../PropertyModal/shared";
import { deleteAgencyTaskAssigneeAsync } from "../../redux/tasks/tasksSlice";
import { normalizeAssigneeKey } from "../../utils/taskAssignees";

export default function AssigneeSelect({
	options,
	value,
	onChange,
	onCreateOption,
	placeholder,
	formatCreateLabel,
	noOptionsMessage,
	isLoading = false,
	isInvalid = false,
	borderColor,
	menuPortalTarget,
	menuPosition,
	styles,
	chakraStyles,
	onDeleted,
}) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const toast = useToast();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [pendingDelete, setPendingDelete] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const closeConfirm = () => {
		if (deleting) return;
		onClose();
		setPendingDelete(null);
	};

	const requestDelete = useCallback(
		(option) => {
			setPendingDelete(option);
			onOpen();
		},
		[onOpen]
	);

	const confirmDelete = () => {
		if (!pendingDelete) return;
		const normalizedName = pendingDelete.normalizedName || normalizeAssigneeKey(pendingDelete.value || pendingDelete.label);
		if (!normalizedName) return;

		setDeleting(true);
		dispatch(deleteAgencyTaskAssigneeAsync(normalizedName)).then((result) => {
			setDeleting(false);
			if (result.error) {
				toast({
					title: t("tasks.deleteAssigneeError"),
					description: result.payload || result.error.message,
					status: "error",
					duration: 5000,
					isClosable: true,
				});
				return;
			}
			toast({
				title: t("tasks.deleteAssigneeSuccess"),
				status: "success",
				duration: 2000,
				isClosable: true,
			});
			onDeleted?.(normalizedName, pendingDelete.value || pendingDelete.label);
			onClose();
			setPendingDelete(null);
		});
	};

	const components = useMemo(
		() => ({
			Option: (props) => {
				const { data, children } = props;
				if (data?.__isNew__) {
					return <chakraComponents.Option {...props}>{children}</chakraComponents.Option>;
				}

				return (
					<chakraComponents.Option {...props}>
						<HStack justify="space-between" w="100%" spacing={2}>
							<Box flex="1" minW={0}>
								{children}
							</Box>
							<Tooltip label={t("tasks.removeAssigneeSuggestion")} hasArrow>
								<IconButton
									aria-label={t("tasks.removeAssigneeSuggestion")}
									icon={<CloseIcon boxSize={2.5} />}
									size="xs"
									variant="ghost"
									colorScheme="red"
									flexShrink={0}
									onMouseDown={(event) => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
										requestDelete(data);
									}}
								/>
							</Tooltip>
						</HStack>
					</chakraComponents.Option>
				);
			},
		}),
		[requestDelete, t]
	);

	const mergedChakraStyles = {
		...chakraStyles,
		control: (provided, state) => {
			const base = chakraStyles?.control ? chakraStyles.control(provided, state) : provided;
			return {
				...base,
				borderRadius: base.borderRadius || "8px",
				...(isInvalid ? { borderColor: "red.400" } : borderColor ? { borderColor } : null),
			};
		},
	};

	return (
		<>
			<CreatableSelect
				isClearable
				isSearchable
				isLoading={isLoading}
				options={options}
				value={value}
				onChange={onChange}
				onCreateOption={onCreateOption}
				placeholder={placeholder}
				formatCreateLabel={formatCreateLabel}
				noOptionsMessage={typeof noOptionsMessage === "function" ? noOptionsMessage : () => noOptionsMessage}
				components={components}
				menuPortalTarget={menuPortalTarget}
				menuPosition={menuPosition}
				styles={styles}
				chakraStyles={mergedChakraStyles}
			/>
			<PropertyConfirmModal
				isOpen={isOpen}
				onClose={closeConfirm}
				onConfirm={confirmDelete}
				title={t("tasks.removeAssigneeSuggestion")}
				message={t("tasks.deleteAssigneeConfirmation")}
				emphasis={t("tasks.deleteAssigneeEmphasis")}
				isLoading={deleting}
			/>
		</>
	);
}
